/* A local controller; native video controls remain the no-JavaScript fallback. */
(() => {
  'use strict';
  if (customElements.get('vn-film')) return;

  class VidNaturFilm extends HTMLElement {
    connectedCallback() {
      cancelAnimationFrame(this.frame);
      this.frame = requestAnimationFrame(() => this.initialise());
    }

    disconnectedCallback() {
      cancelAnimationFrame(this.frame);
      this.cleanup?.();
      this.cleanup = null;
    }

    initialise() {
      if (!this.isConnected || this.cleanup) return;
      const video = this.querySelector('video');
      const button = this.querySelector('.vn-film__toggle');
      const media = this.querySelector('.vn-film__media');
      const status = this.querySelector('.vn-film__status');
      if (!video || !button || !media) return;

      this.setAttribute('data-vn-film', '');
      const motion = matchMedia('(prefers-reduced-motion: reduce)');
      const canObserve = 'IntersectionObserver' in window;
      let visible = !canObserve;
      let manualPlayback = false;
      let pending = false;
      let disposed = false;
      let revision = 0;
      let observer;

      video.autoplay = false;
      video.removeAttribute('autoplay');
      video.muted = true;
      video.defaultMuted = true;
      video.loop = true;
      video.playsInline = true;
      video.setAttribute('playsinline', '');
      video.preload = 'metadata';
      button.type = 'button';
      if (video.id) button.setAttribute('aria-controls', video.id);

      const updateButton = () => {
        const label = video.paused ? 'Reproducir vídeo' : 'Pausar vídeo';
        const text = button.querySelector('[data-vn-film-label]') || button;
        text.textContent = label;
        button.setAttribute('aria-label', label);
        button.setAttribute('data-playing', video.paused ? 'false' : 'true');
        if (status && !video.paused) status.textContent = '';
      };

      const shouldPlay = () => visible && !document.hidden && !this.userPaused &&
        (manualPlayback || (canObserve && !motion.matches));

      const sync = () => {
        if (disposed) return;
        const requestedAt = ++revision;
        if (!shouldPlay()) {
          video.pause();
          updateButton();
          return;
        }
        if (!video.paused || pending) return;
        pending = true;
        Promise.resolve().then(() => {
          if (!disposed && shouldPlay()) return video.play();
        }).then(() => {
          if (!disposed && !shouldPlay()) video.pause();
        }).catch((error) => {
          if (!disposed && error.name !== 'AbortError' && status) {
            status.textContent = 'Pulsa Reproducir vídeo para iniciar.';
          }
        }).finally(() => {
          pending = false;
          if (!disposed) {
            updateButton();
            // Resume a visibility change that arrived while play() was pending.
            if (revision !== requestedAt && shouldPlay() && video.paused) sync();
          }
        });
      };

      const toggle = () => {
        if (!video.paused) {
          this.userPaused = true;
          manualPlayback = false;
        } else {
          this.userPaused = false;
          manualPlayback = true;
          // An explicit click can precede the observer's next delivery.
          visible = true;
        }
        sync();
      };

      const visibilityChanged = () => {
        if (document.hidden) manualPlayback = false;
        sync();
      };

      const motionChanged = () => {
        if (motion.matches) manualPlayback = false;
        sync();
      };

      button.addEventListener('click', toggle);
      video.addEventListener('play', updateButton);
      video.addEventListener('pause', updateButton);
      document.addEventListener('visibilitychange', visibilityChanged);
      if (motion.addEventListener) motion.addEventListener('change', motionChanged);
      else motion.addListener(motionChanged);

      if (canObserve) {
        observer = new IntersectionObserver((entries) => {
          visible = entries[0].isIntersecting && entries[0].intersectionRatio >= .2;
          if (!visible) manualPlayback = false;
          sync();
        }, { threshold: .2 });
        observer.observe(media);
      }

      this.cleanup = () => {
        disposed = true;
        observer?.disconnect();
        button.removeEventListener('click', toggle);
        video.removeEventListener('play', updateButton);
        video.removeEventListener('pause', updateButton);
        document.removeEventListener('visibilitychange', visibilityChanged);
        if (motion.removeEventListener) motion.removeEventListener('change', motionChanged);
        else motion.removeListener(motionChanged);
        video.pause();
        video.controls = true;
        button.hidden = true;
      };

      video.controls = false;
      button.hidden = false;
      updateButton();
      sync();
    }
  }

  customElements.define('vn-film', VidNaturFilm);
})();
